// frontend/src/App.js
import { useState, useEffect } from "react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { 
  Users, BookOpen,DollarSign, TrendingUp, Filter
} from "lucide-react";
import axios from "axios";
import { useLocation, Link } from 'react-router-dom';

// API base URL
const API_URL = process.env.REACT_APP_API_URL;

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalCourses: 0,
    totalRevenue: 0,
    approvalRate: 0,
  });
  
  const [courses, setCourses] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [channelData, setChannelData] = useState([]);  
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  const location = useLocation();

  useEffect(() => {

    // Fetch all dashboard data
    const fetchDashboardData = async () => {
      // Use HTTP-only auth cookie; ensure axios sends credentials
      axios.defaults.withCredentials = true;
      setLoading(true);
      try {
        // Fetch stats (cookie-based auth)
        const statsRes = await axios.get(`${API_URL}/dashboard/stats`, { withCredentials: true });
        setStats(statsRes.data);
        
        // Fetch monthly registrations
        const registrationsRes = await axios.get(`${API_URL}/dashboard/registrations`, {
          params: { year: filterYear },
          withCredentials: true
        });
        setRegistrations(registrationsRes.data);
        
        // Fetch channel distribution
        const channelsRes = await axios.get(`${API_URL}/dashboard/channels`, { withCredentials: true });
        setChannelData(channelsRes.data);
        
        // Fetch courses with registration counts
        const coursesRes = await axios.get(`${API_URL}/dashboard/courses`, { withCredentials: true });
        setCourses(coursesRes.data);
              
        // Fetch pending approvals
        const approvalsRes = await axios.get(`${API_URL}/dashboard/pending-approvals`, { withCredentials: true });
        setPendingApprovals(approvalsRes.data);

        setLoading(false);

        // Handle hash navigation after data is loaded and component is rendered
        setTimeout(() => {
          if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
              element.scrollIntoView();
            }
          }
        }, 100);      
          
      } catch (error) {        
        setLoading(false);
      }
    };    
    
    fetchDashboardData();
  }, [filterYear, location.hash]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">  
        {/* Header */}
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto py-4 px-4 sm:px-4 lg:px-4 flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">
              แดชบอร์ด
            </h1>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter size={20} className="text-gray-500" />
                  <select 
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filterYear}
                    onChange={(e) => setFilterYear(parseInt(e.target.value))}
                  >
                    <option value={2024}>2024</option>
                    <option value={2025}>2025</option>
                  </select>
                </div>
              </div>
          </div>
        </header>

        <main className="flex-1">
          <div className="max-w-7xl mx-auto py-4 ">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="font-medium text-gray-500 truncate">จำนวนผู้ลงทะเบียน</dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-gray-900">{Number(stats.totalStudents).toLocaleString()}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                      <BookOpen className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="font-medium text-gray-500 truncate">จำนวนหลักสูตร</dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-gray-900">{Number(stats.totalCourses).toLocaleString()}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="font-medium text-gray-500 truncate">รายได้ทั้งหมด</dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-gray-900">฿{Number(stats.totalRevenue)?.toLocaleString()}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="font-medium text-gray-500 truncate">อัตราการอนุมัติ</dt>
                        <dd className="flex items-baseline">
                          <div className="text-2xl font-semibold text-gray-900">{stats.approvalRate}%</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              {/* Monthly Registrations */}
              <div className="bg-white shadow rounded-lg p-4">
                <h2 className="text-lg font-medium text-gray-900 mb-4">การลงทะเบียนรายเดือน</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={registrations}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="จำนวนผู้ลงทะเบียน" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Registration Channels */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">ช่องทางการรับรู้</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={channelData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {channelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Upcoming Courses */}
            <div className="bg-white shadow rounded-lg p-6 mb-4">
              <h2 className="text-lg font-medium text-gray-900 mb-4">หลักสูตรที่กำลังจะมาถึง</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">หลักสูตร</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">วันที่อบรม</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">ราคา</th>
                      <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">ผู้ลงทะเบียน</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {courses
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .filter(course => new Date(course.date) >= new Date())
                      .map((course) => (
                        <tr key={course.id}>
                          <td className="px-6 py-2 whitespace-nowrap font-medium text-gray-900">
                            <Link to={`/panel/course/view/${course.id}`} 
                              className="text-gray-500 underline decoration-dotted">
                              {course.topic}
                            </Link>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">
                            {new Date(course.date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">฿{Number(course.price).toLocaleString()}</td>
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">
                            <div className="flex items-center">
                              <span className="px-2 inline-flex leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {course.students}
                              </span>
                              <span className="ml-2 text-gray-500">/ {course.maxSeats}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>


            {/*Pending Approvals*/}
            <div id="pending_approvals" className="bg-white shadow rounded-lg p-6 mt-4">
              <h2 className="text-lg font-medium text-gray-900 mb-4">รายการลงทะเบียนรอการอนุมัติ</h2>
              {pendingApprovals.length === 0 ? (
                <div className="text-center py-8 text-gray-500">ไม่มีรายการรอการอนุมัติ</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">ชื่อ-นามสกุล</th>                        
                        <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">บริษัท</th>
                        <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">หลักสูตร</th>                        
                        <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">วันที่ลงทะเบียน</th>                        
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingApprovals.map((registration) => (
                        <tr key={registration.custId}>
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">
                            <Link to={`/panel/course/view/${registration.courseId}`} 
                              className="text-gray-500 underline decoration-dotted">
                              {registration.firstName} {registration.lastName}
                            </Link>                            
                          </td>  
                                                    
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">
                            {registration.company || "-"}
                          </td>
                                                    
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">
                            {registration.courseName}
                          </td>
                                                  
                          <td className="px-6 py-2 whitespace-nowrap text-gray-500">                            
                            {new Date(registration.registerDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </main>
    </div>
  );
}